import {useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import axios from 'axios';
import {
  AccessCodeStatus,
  AddressSuggestion,
  Annuaire,
  AnnuaireInterne,
  ApiAuthorizationOverrides,
  ApiIpRestriction,
  ApiLimitation,
  ApiNumberIpRestriction,
  ApiNumberIpRestrictionUpdate,
  ApiVoicemailGreeting,
  ApiVoicemailMessage,
  CallDirection,
  CallsPage,
  ConferenceRooms,
  LocalisationsUrgence,
  PbxGroupes,
  PlagesHoraires,
  SuiviConsommationClassement,
  SuiviConsommationTelHD,
  TeamsRecord,
  TrunkNumeros,
  TrunkSipConsommation,
  VirtualPbxActivite,
  VirtualPbxNumeros,
  ajouterBlacklistEntrant,
  ajouterLocalisationUrgence,
  changerAccessCode,
  getAnnuaire,
  getAnnuaireInterne,
  getAccessCodeStatus,
  getBlacklistEntrants,
  getClirAssignations,
  getConferenceRooms,
  getAuthorizationOverrides,
  getCallsPage,
  getConfigurationAntiSpamAppel,
  getConfigurationTrunkSip,
  getHistoriqueAppels,
  getIpRestriction,
  getIvrs,
  getLimitation,
  getLocalisationsUrgence,
  getModelesMateriel,
  getNumberIpRestriction,
  getNumeroDetail,
  getNumeroPassword,
  getSwissBuildingReference,
  searchAddresses,
  getPbxGroupes,
  getPlagesHoraires,
  getSipsSrtpStatut,
  getSons,
  getSuiviConsommationTelHD,
  getTeamsRecord,
  getTelephonesIp,
  getTelephonieServices,
  getTicketsAppelsEntrants,
  getToIpFreeContacts,
  getToIpFreeNumbers,
  getTrunkNumeros,
  getTrunkSipConsommation,
  getUtilisateursNumeroCourtInvalide,
  getVirtualNumbers,
  getVirtualPbxActivite,
  getVirtualPbxNumeros,
  getVoiceMailConfig,
  getVoicemailGreeting,
  getVoicemailMessages,
  getVoicemailMessageAudio,
  deleteVoicemailMessage,
  modifierLocalisationUrgence,
  modifierSalleConference,
  putAuthorizationOverrides,
  putIpRestriction,
  putLimitation,
  putNumberIpRestriction,
  saveConfigurationTrunkSip,
  saveSipsSrtpStatut,
  saveTeamsRecord,
  saveVoiceMailConfig,
  supprimerBlacklistEntrant,
  supprimerLocalisationUrgence,
  updateNumero,
  updateVoicemailGreeting,
} from '../Api/telephonie';
import {
  AppelHistorique,
  AssignationClir,
  ConfigurationAntiSpamAppel,
  ConfigurationBoiteVocale,
  ConfigurationTrunkSip,
  ContactToIp,
  EntreeBlacklistEntrant,
  FichierSon,
  Ivr,
  LocalisationSite,
  MaterielIp,
  ModeleMateriel,
  SipsSrtpStatut,
  TicketAppelEntrant,
  TypeSon,
  UtilisateurNumeroCourtInvalide,
} from '../Components/Telephonie/TrunkSip/mocks';
import {ListService} from '../Types/service';
import {getTelephonieUsage, getTelephonieContext, getTelephonieDetails, getParc4G} from '../Api/api';
import {TelephonyUsageResponse,TelephonyCalls} from '../Types/type';

/**
 * Suivi de consommation's reads (outbound/inbound/limitation, all hitting the
 * legacy VTX SQL Server) occasionally 500 under the load of their own
 * concurrent first-page-load calls, then succeed immediately when replayed —
 * transient DB-side contention, not a real failure. The global QueryClient
 * default is `retry: false`, so nothing recovers on its own; this bounded
 * retry is opt-in per hook rather than changed globally.
 */
const RETRY_ON_TRANSIENT_FAILURE = {
  retry: (failureCount: number, error: unknown): boolean =>
    failureCount < 2 && axios.isAxiosError(error) && (error.response?.status ?? 0) >= 500,
  retryDelay: 800,
};

const TEAMS_RECORD_QUERY_KEY = 'centrex-teams-record';
const CONFERENCE_ROOMS_QUERY_KEY = 'centrex-conference-rooms';
const VOICE_MAIL_QUERY_KEY = 'centrex-voicemail-config';
const ACCESS_CODE_STATUS_QUERY_KEY = 'centrex-access-code-status';
const CLIR_ASSIGNATIONS_QUERY_KEY = 'centrex-clir-assignations';
const IVRS_QUERY_KEY = 'centrex-ivrs';
const TELEPHONES_IP_QUERY_KEY = 'centrex-telephones-ip';
const SIPS_SRTP_QUERY_KEY = 'centrex-sips-srtp';
const sonsQueryKey = (type: TypeSon): string => `centrex-sons-${type}`;

export const useGetTelephonieServices = () =>
  useQuery<ListService[], unknown, ListService[]>(
    'telephonie-services',
    () => getTelephonieServices(),
    {refetchOnMount: false}
  );

export const useGetSuiviConsommationTelHD = (
  period: string,
  classement: SuiviConsommationClassement
) =>
  useQuery<SuiviConsommationTelHD, unknown, SuiviConsommationTelHD>(
    ['suivi-consommation-tel-hd', period, classement],
    () => getSuiviConsommationTelHD(period, classement),
    {refetchOnMount: false}
  );

export const useGetTrunkSipConsommation = (serviceId: number, period: string) =>
  useQuery<TrunkSipConsommation, unknown, TrunkSipConsommation>(
    ['trunk-sip-consommation', serviceId, period],
    () => getTrunkSipConsommation(serviceId, period),
    {refetchOnMount: false, ...RETRY_ON_TRANSIENT_FAILURE}
  );

export const useGetCallsPage = (
  serviceId: number,
  period: string,
  direction: CallDirection,
  page: number,
  itemsPerPage: number,
  caller?: string,
  order?: 'asc' | 'desc'
) =>
  useQuery<CallsPage, unknown, CallsPage>(
    ['trunk-sip-calls-page', serviceId, period, direction, page, itemsPerPage, caller, order],
    () => getCallsPage(serviceId, period, direction, page, itemsPerPage, caller, order),
    {refetchOnMount: false, keepPreviousData: true, ...RETRY_ON_TRANSIENT_FAILURE}
  );

const ANTISPAM_APPEL_QUERY_KEY = 'trunk-sip-antispam-appel';

const blacklistEntrantsQueryKey = (serviceId: number): [string, number] => [
  'trunk-sip-blacklist-entrants',
  serviceId,
];

export const useGetBlacklistEntrants = (serviceId: number, enabled = true) =>
  useQuery<EntreeBlacklistEntrant[], unknown, EntreeBlacklistEntrant[]>(
    blacklistEntrantsQueryKey(serviceId),
    () => getBlacklistEntrants(serviceId),
    {refetchOnMount: false, enabled}
  );

export const useAjouterBlacklistEntrant = (serviceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({numeroAppelant, numeroAppele}: {numeroAppelant: string; numeroAppele: string}) =>
      ajouterBlacklistEntrant(serviceId, numeroAppelant, numeroAppele),
    onSuccess: () => {
      queryClient.invalidateQueries(blacklistEntrantsQueryKey(serviceId));
    },
  });
};

export const useSupprimerBlacklistEntrant = (serviceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => supprimerBlacklistEntrant(serviceId, id),
    onSuccess: () => {
      queryClient.invalidateQueries(blacklistEntrantsQueryKey(serviceId));
    },
  });
};

export const useGetVirtualNumbers = (serviceId: number) =>
  useQuery<string[], unknown, string[]>(
    ['trunk-sip-virtual-numbers', serviceId],
    () => getVirtualNumbers(serviceId),
    {refetchOnMount: false}
  );

export const useGetTicketsAppelsEntrants = (
  serviceId: number,
  periode: string,
  numeroAppele: string | undefined,
  enabled: boolean
) =>
  useQuery<TicketAppelEntrant[], unknown, TicketAppelEntrant[]>(
    ['trunk-sip-tickets-entrants', serviceId, periode, numeroAppele],
    () => getTicketsAppelsEntrants(serviceId, periode, numeroAppele),
    {refetchOnMount: false, enabled}
  );

export const useGetConfigurationAntiSpamAppel = () =>
  useQuery<ConfigurationAntiSpamAppel, unknown, ConfigurationAntiSpamAppel>(
    ANTISPAM_APPEL_QUERY_KEY,
    () => getConfigurationAntiSpamAppel(),
    {refetchOnMount: false}
  );

const limitationQueryKey = (serviceId: number): [string, number] => [
  'trunk-sip-limitation',
  serviceId,
];

export const useGetLimitation = (serviceId: number, enabled = true) =>
  useQuery<ApiLimitation, unknown, ApiLimitation>(
    limitationQueryKey(serviceId),
    () => getLimitation(serviceId),
    {refetchOnMount: false, enabled, ...RETRY_ON_TRANSIENT_FAILURE}
  );

/**
 * `usage` (la jauge de forfait) dépend du plafond de la limitation : toute
 * mise à jour invalide aussi la query de consommation trunk SIP, quelle que
 * soit la période affichée.
 */
export const useUpdateLimitation = (serviceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (limitation: ApiLimitation) => putLimitation(serviceId, limitation),
    onSuccess: (data) => {
      queryClient.setQueryData(limitationQueryKey(serviceId), data);
      queryClient.invalidateQueries(['trunk-sip-consommation', serviceId]);
    },
  });
};

const authorizationOverridesQueryKey = (
  serviceId: number,
  numberId: number
): [string, number, number] => ['trunk-sip-authorization-overrides', serviceId, numberId];

export const useGetAuthorizationOverrides = (serviceId: number, numberId: number, enabled = true) =>
  useQuery<ApiAuthorizationOverrides, unknown, ApiAuthorizationOverrides>(
    authorizationOverridesQueryKey(serviceId, numberId),
    () => getAuthorizationOverrides(serviceId, numberId),
    {refetchOnMount: false, enabled}
  );

export const useUpdateAuthorizationOverrides = (serviceId: number, numberId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (authorizationOverrides: ApiAuthorizationOverrides) =>
      putAuthorizationOverrides(serviceId, numberId, authorizationOverrides),
    onSuccess: (data) => {
      queryClient.setQueryData(authorizationOverridesQueryKey(serviceId, numberId), data);
    },
  });
};

const ipRestrictionQueryKey = (serviceId: number): [string, number] => [
  'trunk-sip-ip-restriction',
  serviceId,
];

export const useGetIpRestriction = (serviceId: number, enabled = true) =>
  useQuery<ApiIpRestriction, unknown, ApiIpRestriction>(
    ipRestrictionQueryKey(serviceId),
    () => getIpRestriction(serviceId),
    {refetchOnMount: false, enabled}
  );

export const useUpdateIpRestriction = (serviceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ipRestriction: ApiIpRestriction) => putIpRestriction(serviceId, ipRestriction),
    onSuccess: (data) => {
      queryClient.setQueryData(ipRestrictionQueryKey(serviceId), data);
    },
  });
};

const numberIpRestrictionQueryKey = (
  serviceId: number,
  numberId: number
): [string, number, number] => ['trunk-sip-number-ip-restriction', serviceId, numberId];

export const useGetNumberIpRestriction = (serviceId: number, numberId: number, enabled = true) =>
  useQuery<ApiNumberIpRestriction, unknown, ApiNumberIpRestriction>(
    numberIpRestrictionQueryKey(serviceId, numberId),
    () => getNumberIpRestriction(serviceId, numberId),
    {refetchOnMount: false, enabled}
  );

export const useUpdateNumberIpRestriction = (serviceId: number, numberId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: ApiNumberIpRestrictionUpdate) =>
      putNumberIpRestriction(serviceId, numberId, update),
    onSuccess: (data) => {
      queryClient.setQueryData(numberIpRestrictionQueryKey(serviceId, numberId), data);
    },
  });
};

const localisationsUrgenceQueryKey = (serviceId?: number): [string, number | undefined] => [
  'trunk-sip-localisations-urgence',
  serviceId,
];

export const useGetLocalisationsUrgence = (serviceId?: number, enabled = true) =>
  useQuery<LocalisationsUrgence, unknown, LocalisationsUrgence>(
    localisationsUrgenceQueryKey(serviceId),
    () => getLocalisationsUrgence(serviceId),
    {refetchOnMount: false, enabled}
  );

export const useAjouterLocalisationUrgence = (serviceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (site: Omit<LocalisationSite, 'id'>) => ajouterLocalisationUrgence(serviceId, site),
    onSuccess: () => queryClient.invalidateQueries(localisationsUrgenceQueryKey(serviceId)),
  });
};

export const useModifierLocalisationUrgence = (serviceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, site}: {id: number; site: Omit<LocalisationSite, 'id'>}) =>
      modifierLocalisationUrgence(serviceId, id, site),
    onSuccess: () => queryClient.invalidateQueries(localisationsUrgenceQueryKey(serviceId)),
  });
};

export const useSupprimerLocalisationUrgence = (serviceId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => supprimerLocalisationUrgence(serviceId, id),
    onSuccess: () => queryClient.invalidateQueries(localisationsUrgenceQueryKey(serviceId)),
  });
};

const ADDRESS_SEARCH_QUERY_KEY = 'egls-address-search';

/**
 * Autocomplete d'adresse (EGLS), déclenchée à chaque frappe : debounce
 * manuel 300ms (même technique que `AutocompleteBu`) avant d'interroger le
 * référentiel. `zipcode`/`city` affinent la recherche s'ils sont déjà
 * renseignés dans le formulaire.
 */
export const useSearchAddresses = (
  term: string,
  country: 'FR' | 'CH',
  enabled: boolean,
  zipcode?: string,
  city?: string
) => {
  const [debouncedTerm, setDebouncedTerm] = useState(term);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setDebouncedTerm(term), 300);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [term]);

  return useQuery<AddressSuggestion[], unknown, AddressSuggestion[]>(
    [ADDRESS_SEARCH_QUERY_KEY, debouncedTerm, country, zipcode, city],
    () => searchAddresses(debouncedTerm, country, zipcode, city),
    {enabled: enabled && debouncedTerm.trim().length > 2, staleTime: 30000, retry: false}
  );
};

/** Résout municipalityId/egid pour une suggestion suisse, une fois sélectionnée. */
export const useSwissBuildingReference = () =>
  useMutation((featureId: string) => getSwissBuildingReference(featureId));

export const useGetTrunkNumeros = (serviceId?: number, enabled = true) =>
  useQuery<TrunkNumeros, unknown, TrunkNumeros>(
    ['trunk-sip-numeros', serviceId],
    () => getTrunkNumeros(serviceId),
    {refetchOnMount: false, enabled}
  );

/** Détail d'un numéro (loupe « Consulter »), déclenché à la demande plutôt qu'au montage. */
export const useGetNumeroDetail = () =>
  useMutation(({serviceId, numeroId}: {serviceId: number; numeroId: number}) =>
    getNumeroDetail(serviceId, numeroId)
  );

/** Révélation à la demande du mot de passe SIP/softphone actuel — jamais mise en cache. */
export const useGetNumeroPassword = () =>
  useMutation(
    ({
      serviceId,
      numeroId,
      target,
    }: {
      serviceId: number;
      numeroId: number;
      target: 'sip' | 'softphone';
    }) => getNumeroPassword(serviceId, numeroId, target)
  );

const voicemailGreetingQueryKey = (
  serviceId: number,
  numeroId: number
): [string, number, number] => ['trunk-sip-voicemail-greeting', serviceId, numeroId];

export const useGetVoicemailGreeting = (serviceId: number, numeroId: number, enabled = true) =>
  useQuery<ApiVoicemailGreeting, unknown, ApiVoicemailGreeting>(
    voicemailGreetingQueryKey(serviceId, numeroId),
    () => getVoicemailGreeting(serviceId, numeroId),
    {refetchOnMount: false, enabled}
  );

export const useUpdateVoicemailGreeting = (serviceId: number, numeroId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (audioContent: string) =>
      updateVoicemailGreeting(serviceId, numeroId, audioContent),
    onSuccess: (data) => {
      queryClient.setQueryData(voicemailGreetingQueryKey(serviceId, numeroId), data);
    },
  });
};

const voicemailMessagesQueryKey = (
  serviceId: number,
  numeroId: number
): [string, number, number] => ['trunk-sip-voicemail-messages', serviceId, numeroId];

export const useGetVoicemailMessages = (serviceId: number, numeroId: number, enabled = true) =>
  useQuery<ApiVoicemailMessage[], unknown, ApiVoicemailMessage[]>(
    voicemailMessagesQueryKey(serviceId, numeroId),
    () => getVoicemailMessages(serviceId, numeroId),
    {refetchOnMount: false, enabled}
  );

/** Lecture à la demande : chaque message n'est téléchargé que lorsque l'utilisateur l'écoute. */
export const useGetVoicemailMessageAudio = (serviceId: number, numeroId: number) =>
  useMutation((messageId: number) => getVoicemailMessageAudio(serviceId, numeroId, messageId));

export const useDeleteVoicemailMessage = (serviceId: number, numeroId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: number) => deleteVoicemailMessage(serviceId, numeroId, messageId),
    onSuccess: () => queryClient.invalidateQueries(voicemailMessagesQueryKey(serviceId, numeroId)),
  });
};

/**
 * Modification d'un numéro (localisation, mot de passe SIP), persistée côté api-telephonie.
 * `invalidateOnSuccess: false` permet à un appelant qui enchaîne plusieurs mises à jour (ex.
 * affectation en masse à une localisation) de ne rafraîchir la liste des numéros qu'une seule
 * fois à la fin, plutôt qu'après chaque numéro traité.
 */
export const useMettreAJourNumero = (
  serviceId: number,
  options?: {invalidateOnSuccess?: boolean}
) => {
  const queryClient = useQueryClient();
  const invalidateOnSuccess = options?.invalidateOnSuccess ?? true;
  return useMutation({
    mutationFn: ({
      numeroId,
      payload,
    }: {
      numeroId: number;
      payload: Parameters<typeof updateNumero>[2];
    }) => updateNumero(serviceId, numeroId, payload),
    onSuccess: () => {
      if (invalidateOnSuccess) {
        queryClient.invalidateQueries(['trunk-sip-numeros', serviceId]);
      }
    },
    // api-telephonie répond parfois « The remote service could not be reached »
    // sous charge (ex. affectation en masse de numéros à une localisation) ;
    // on retente avant d'abandonner plutôt que de faire échouer l'opération.
    retry: 2,
    retryDelay: (attempt) => 1000 * 2 ** attempt,
  });
};

export const useGetAnnuaire = () =>
  useQuery<Annuaire, unknown, Annuaire>('trunk-sip-annuaire', () => getAnnuaire(), {
    refetchOnMount: false,
  });

export const useGetAnnuaireInterne = () =>
  useQuery<AnnuaireInterne, unknown, AnnuaireInterne>(
    'centrex-annuaire-interne',
    () => getAnnuaireInterne(),
    {refetchOnMount: false}
  );

export const useGetPlagesHoraires = () =>
  useQuery<PlagesHoraires, unknown, PlagesHoraires>(
    'centrex-pbx-plages-horaires',
    () => getPlagesHoraires(),
    {refetchOnMount: false}
  );

export const useGetPbxGroupes = () =>
  useQuery<PbxGroupes, unknown, PbxGroupes>('centrex-pbx-groupes', () => getPbxGroupes(), {
    refetchOnMount: false,
  });

export const useGetIvrs = () =>
  useQuery<Ivr[], unknown, Ivr[]>(IVRS_QUERY_KEY, () => getIvrs(), {refetchOnMount: false});

export const useGetVirtualPbxNumeros = () =>
  useQuery<VirtualPbxNumeros, unknown, VirtualPbxNumeros>(
    'centrex-pbx-numeros',
    () => getVirtualPbxNumeros(),
    {refetchOnMount: false}
  );

export const useGetVirtualPbxActivite = () =>
  useQuery<VirtualPbxActivite, unknown, VirtualPbxActivite>(
    'centrex-pbx-activite',
    () => getVirtualPbxActivite(),
    {refetchOnMount: false}
  );

export const useGetTeamsRecord = () =>
  useQuery<TeamsRecord, unknown, TeamsRecord>(TEAMS_RECORD_QUERY_KEY, () => getTeamsRecord(), {
    refetchOnMount: false,
  });

export const useSaveTeamsRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (txtRecord: string) => saveTeamsRecord(txtRecord),
    onSuccess: (data) => {
      queryClient.setQueryData(TEAMS_RECORD_QUERY_KEY, data);
    },
  });
};

export const useGetConferenceRooms = () =>
  useQuery<ConferenceRooms, unknown, ConferenceRooms>(
    CONFERENCE_ROOMS_QUERY_KEY,
    () => getConferenceRooms(),
    {refetchOnMount: false}
  );

export const useModifierSalleConference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, nom}: {id: number; nom: string}) => modifierSalleConference(id, nom),
    onSuccess: (resultat) => {
      const actuel = queryClient.getQueryData<ConferenceRooms>(CONFERENCE_ROOMS_QUERY_KEY);
      if (actuel) {
        queryClient.setQueryData(CONFERENCE_ROOMS_QUERY_KEY, {
          salles: actuel.salles.map((salle) =>
            salle.id === resultat.id ? {...salle, nom: resultat.nom} : salle
          ),
        });
      }
    },
  });
};

export const useGetVoiceMailConfig = () =>
  useQuery<ConfigurationBoiteVocale, unknown, ConfigurationBoiteVocale>(
    VOICE_MAIL_QUERY_KEY,
    () => getVoiceMailConfig(),
    {refetchOnMount: false}
  );

export const useSaveVoiceMailConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configuration: ConfigurationBoiteVocale) => saveVoiceMailConfig(configuration),
    onSuccess: (data) => {
      queryClient.setQueryData(VOICE_MAIL_QUERY_KEY, data);
    },
  });
};

export const useGetSons = (type: TypeSon) =>
  useQuery<FichierSon[], unknown, FichierSon[]>(sonsQueryKey(type), () => getSons(type), {
    refetchOnMount: false,
  });

export const useGetAccessCodeStatus = () =>
  useQuery<AccessCodeStatus, unknown, AccessCodeStatus>(
    ACCESS_CODE_STATUS_QUERY_KEY,
    () => getAccessCodeStatus(),
    {refetchOnMount: false}
  );

export const useGetUtilisateursNumeroCourtInvalide = () =>
  useQuery<UtilisateurNumeroCourtInvalide[], unknown, UtilisateurNumeroCourtInvalide[]>(
    'centrex-numeros-courts-invalides',
    () => getUtilisateursNumeroCourtInvalide(),
    {refetchOnMount: false}
  );

export const useChangerAccessCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      actif,
      changements,
    }: {
      actif: boolean;
      changements: {id: number; numeroCourt: string}[];
    }) => changerAccessCode(actif, changements),
    onSuccess: (resultat) => {
      queryClient.setQueryData(ACCESS_CODE_STATUS_QUERY_KEY, {actif: resultat.actif});
    },
  });
};

export const useGetClirAssignations = () =>
  useQuery<AssignationClir[], unknown, AssignationClir[]>(
    CLIR_ASSIGNATIONS_QUERY_KEY,
    () => getClirAssignations(),
    {refetchOnMount: false}
  );

export const useGetTelephonesIp = () =>
  useQuery<MaterielIp[], unknown, MaterielIp[]>(TELEPHONES_IP_QUERY_KEY, () => getTelephonesIp(), {
    refetchOnMount: false,
  });

export const useGetModelesMateriel = () =>
  useQuery<ModeleMateriel[], unknown, ModeleMateriel[]>(
    'centrex-modeles-materiel',
    () => getModelesMateriel(),
    {refetchOnMount: false}
  );

export const useGetToIpFreeNumbers = () =>
  useQuery<string[], unknown, string[]>('centrex-toip-free-numbers', () => getToIpFreeNumbers(), {
    refetchOnMount: false,
  });

export const useGetToIpFreeContacts = () =>
  useQuery<ContactToIp[], unknown, ContactToIp[]>(
    'centrex-toip-free-contacts',
    () => getToIpFreeContacts(),
    {refetchOnMount: false}
  );

export const useGetSipsSrtpStatut = () =>
  useQuery<SipsSrtpStatut, unknown, SipsSrtpStatut>(
    SIPS_SRTP_QUERY_KEY,
    () => getSipsSrtpStatut(),
    {refetchOnMount: false}
  );

export const useSaveSipsSrtpStatut = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (actif: boolean) => saveSipsSrtpStatut(actif),
    onSuccess: (data) => {
      queryClient.setQueryData(SIPS_SRTP_QUERY_KEY, data);
    },
  });
};

export const useGetHistoriqueAppels = () =>
  useQuery<AppelHistorique[], unknown, AppelHistorique[]>(
    'centrex-historique-appels',
    () => getHistoriqueAppels(),
    {refetchOnMount: false}
  );

const CONFIG_TRUNK_SIP_QUERY_KEY = 'trunk-sip-configuration';

/** `numeroId` = numéro tête de trunk (`TrunkingMainNumber`), résolu par l'appelant. */
export const useGetConfigurationTrunkSip = (serviceId?: number, numeroId?: number) =>
  useQuery<ConfigurationTrunkSip, unknown, ConfigurationTrunkSip>(
    [CONFIG_TRUNK_SIP_QUERY_KEY, serviceId, numeroId],
    () => getConfigurationTrunkSip(serviceId as number, numeroId as number),
    {refetchOnMount: false, enabled: serviceId !== undefined && numeroId !== undefined}
  );

export const useSaveConfigurationTrunkSip = (serviceId: number, numeroId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (configuration: ConfigurationTrunkSip) =>
      saveConfigurationTrunkSip(serviceId, numeroId, configuration),
    onSuccess: (data) => {
      queryClient.setQueryData([CONFIG_TRUNK_SIP_QUERY_KEY, serviceId, numeroId], data);
    },
  });
};

export {
  sonsQueryKey,
  CLIR_ASSIGNATIONS_QUERY_KEY,
  IVRS_QUERY_KEY,
  TELEPHONES_IP_QUERY_KEY,
  ANTISPAM_APPEL_QUERY_KEY,
};

const KEY = 'TELEPHONY'

interface TelephonieDetails{
  outgoings: TelephonyCalls[];
  incomings: TelephonyCalls[];
}

export const useGetTelephonyUsage = (datas:Object) => {
  return useQuery<any, any, TelephonyUsageResponse>([KEY, 'usage', datas],
    async () =>
    getTelephonieUsage(datas)
  );
}

export const useGetTelephonyQuota = () => {
    return useQuery<any, any, any>(
        [KEY, 'quota'],
        () => getTelephonieContext()
    );
}

export const useGetTelephonyDetails = (datas: {category: string, caller?: string , destination?: string , month?: string, serviceId?: string}) => {
    return useQuery<any, any, TelephonieDetails>(
        [KEY, 'details', datas],
        () => getTelephonieDetails(datas)
    );
}

export const useGetParc4G = (dataService:ListService) => {
  return useQuery<any, any, any[] >(
      ['parc4G'],
      async () => getParc4G(dataService)
    );
}
